import { onboardingLog } from './store/logger'

const YA_COUNTER_KEY_RE = /^yaCounter(\d+)$/
const YA_COUNTER_INIT_RE = /ym\((\d+)\s*,\s*['"]init['"]/

let cachedCounterId

/**
 * Resolve Yandex.Metrika counter id from the page (yaCounter* global or script tag).
 * @returns {number | null}
 */
export const getYandexMetrikaCounterId = () => {
  if (cachedCounterId !== undefined) {
    return cachedCounterId
  }

  if (typeof window === 'undefined') {
    return null
  }

  const yaCounterKey = Object.keys(window).find((key) => YA_COUNTER_KEY_RE.test(key))
  if (yaCounterKey) {
    cachedCounterId = Number(yaCounterKey.slice('yaCounter'.length))
    return cachedCounterId
  }

  const metrikaScript = document.getElementById('metrika-counter')
  const counterFromAttr = metrikaScript?.dataset?.counterId
  if (counterFromAttr) {
    cachedCounterId = Number(counterFromAttr)
    return cachedCounterId
  }

  const initMatch = metrikaScript?.textContent?.match(YA_COUNTER_INIT_RE)
  if (initMatch) {
    cachedCounterId = Number(initMatch[1])
    return cachedCounterId
  }

  cachedCounterId = null
  return cachedCounterId
}

const getYandexMetrikaCounter = () => {
  const counterId = getYandexMetrikaCounterId()
  if (!counterId) return null

  return window[`yaCounter${counterId}`] || null
}

/**
 * Send reachGoal events configured on a learning config button.
 * @param {object} button — Button from learningConfigs
 * @param {{ trackId?: string, cardId?: string, pageId?: string }} [context]
 */
export const trackOnboardingButtonAnalytics = (button, context = {}) => {
  const goal =
    button?.analyticsGoal ??
    (button?.action === 'confirm-interrupt' ? 'onboarding_close_click' : null)

  if (!goal) {
    onboardingLog('analytics:skip', {
      reason: 'no analyticsGoal',
      action: button?.action,
      label: button?.label,
      ...context,
    })
    return
  }

  sendOnboardingEvent(goal, {
    trackId: context.trackId,
    cardId: context.cardId,
    pageId: context.pageId,
    ...button.analyticsParams,
  })
}

/**
 * Send onboarding event to Yandex.Metrika and dataLayer.
 * @param {string} eventName - event goal name
 * @param {object} [params] - optional params for the event
 */
export const sendOnboardingEvent = (eventName, params) => {
  const counter = getYandexMetrikaCounter()
  const counterId = getYandexMetrikaCounterId()
  const hasYm = Boolean(counterId && typeof window?.ym === 'function')
  const hasDataLayer = Boolean(global.dataLayer)

  if (counter) {
    counter.reachGoal(eventName, params)
  }

  if (hasYm) {
    window.ym(counterId, 'reachGoal', eventName, params)
  }

  if (hasDataLayer) {
    global.dataLayer.push({ event: eventName, ...params })
  }

  onboardingLog('analytics:send', {
    goal: eventName,
    params,
    counterId,
    channels: {
      yaCounter: Boolean(counter),
      ym: hasYm,
      dataLayer: hasDataLayer,
    },
  })
}
